import React, { useMemo, useRef, useEffect } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { ButtonColor } from '@/components/adg4/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { AiContent } from './ai-content';
import { NavProductRecommendationsContext } from './global-nav-product-recommendations/context/nav-product-recommendations-context';
import { useGlobalNavProductRecommendations } from './global-nav-product-recommendations/hooks/use-global-nav-product-recommendations';
import { GlobalNavProductsDropdown } from './global-nav-products-dropdown';
import { GlobalNavResourcesDropdown } from './global-nav-resources-dropdown';
import { GlobalNavSolutionsDropdown } from './global-nav-solutions-dropdown';
import { GlobalNavWhyAtlassianDropdown } from './global-nav-why-atlassian-dropdown';
import { useMobileNavDisplay } from './hooks/use-mobile-nav-display';
import { useMore } from './hooks/use-more';
import { NAV_SCROLL_SHADOW, useNavScrollShadow } from './hooks/use-nav-scroll-shadow';
import { GlobalNavMobileMenu } from './mobile-nav/global-nav-mobile-menu';
import { MobileNavTop } from './mobile-nav/mobile-nav-top';
import { CompoundSubNav, CompoundSubNavProps } from './nav-global-compound-subnav';
import { navLayout, navGrid } from './nav-layout';
import GlobalNavLinksContainer, {
	GlobalNavLinkContentProps,
	GlobalNavProvider,
	GlobalNavDropdownLayout,
} from './nav-links-container';
import { NavUtilities } from './nav-utilities';
import SearchBar from './search-bar';
import { SkipToContent } from './skip-to-content';

// Retained for existing consumers; geometry lives in nav-layout.
export const GLOBAL_NAV_HEIGHT = navLayout.height;

const navigationShellCss = css({
	'--nav-menu-padding-block-start': token('space.050'),
	backgroundColor: wacToken('color.neutral.light0'),
	paddingInline: navLayout.outerInset,
	paddingBlock: 0,
	boxSizing: 'border-box',
	[media.above.md]: { paddingBlock: 0, height: navLayout.height },
});

const expandedMobileShellCss = css({
	[media.below.sm]: {
		position: 'fixed',
		insetBlockStart: 0,
		insetInlineStart: 0,
		width: '100%',
		height: '100vh',
		overflow: 'auto',
		zIndex: 5,
	},
});

// Compiled supports the calculated grid tracks needed to align with the page grid.
const navigationBarContentCss = css({
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr) auto',
	height: navLayout.height,
	alignItems: 'center',
	position: 'relative',
	[media.above.md]: {
		gridTemplateRows: 'minmax(0, 1fr)',
		height: '100%',
		gridTemplateColumns: navGrid.globalColumnsDesktop,
	},
	[media.above.lg]: {
		gridTemplateColumns: navGrid.globalColumnsWide,
	},
});

const desktopControlsCss = css({
	display: 'none',
	[media.above.md]: { display: 'contents' },
});

const mobileControlsCss = css({
	display: 'flex',
	justifyContent: 'flex-end',
	minWidth: 0,
	[media.above.md]: { '&&': { display: 'none' } },
});

const mobileMenuCss = css({
	display: 'none',
	[media.below.sm]: { display: 'block', marginBlockStart: token('space.150') },
});

const navigationBarSearchCss = css({
	display: 'flex',
	gridColumn: '2 / -1',
	minWidth: 0,
});

const navigationBarLogoCss = css({
	display: 'flex',
	alignItems: 'center',
	paddingBlockStart: token('space.050'),
});

const navigationBarLogoLink = css({
	display: 'flex',
	width: navLayout.atlassianLogoWidth,
	height: navLayout.logoHeight,
	flexShrink: 0,
});

const navigationBarLogoImageCss = css({
	display: 'flex',
	width: '100%',
	height: '100%',
});

const isStickyNavCss = css({
	position: 'sticky',
	insetBlockStart: 0,
});

export type GlobalNavProps = {
	productKey: ProductKeyTypes;
	isShowSearchBarAndProfileContainer?: boolean;
	isShowTryNowLink?: boolean;
	isShowSearchButton?: boolean;
	tryNowLinkCtaButtonColor?: ButtonColor;
	isNavSticky?: boolean;
	tryNowLinkCtaUrl?: string;
	compoundNav?: CompoundSubNavProps;
};

export type GlobalNavNavigationBarProps = GlobalNavProps & {
	globalLinksProps: GlobalNavLinkContentProps[];
};

export const GlobalNavNavigationBar = ({
	productKey,
	isShowSearchBarAndProfileContainer = true,
	isShowTryNowLink = false,
	isShowSearchButton = true,
	tryNowLinkCtaButtonColor,
	globalLinksProps,
	tryNowLinkCtaUrl,
}: GlobalNavNavigationBarProps) => {
	const intl = useIntl();
	const atlasUrl = useLocalizedHref('/');
	const [isOpenSearch, setIsOpenSearch] = React.useState(false);
	const mobileState = useMobileNavDisplay(isOpenSearch);
	const globalLinksRef = useRef<HTMLUListElement>(null);
	const searchAndProfileRef = useRef<HTMLDivElement>(null);
	const hasMounted = useRef(false);
	const navRef = useRef<HTMLElement>(null);

	const {
		showMoreItems: setShowMore,
		shouldShowMoreItems: showMore,
		maxItems: maxLinks,
	} = useMore({
		leftElementRef: globalLinksRef,
		rightElementRef: searchAndProfileRef,
		initialMaxItems: globalLinksProps.length,
		buffer: 24,
	});

	useEffect(() => {
		if (hasMounted.current) {
			const searchButton = Array.from(
				navRef.current?.querySelectorAll<HTMLButtonElement>(
					'#global-nav-search-bar-icon-desktop',
				) ?? [],
			).find((button) => button.getClientRects().length > 0);
			if (searchButton) {
				searchButton.focus();
			}
		} else {
			hasMounted.current = true;
		}
	}, [isOpenSearch]);

	return (
		<nav
			ref={navRef}
			aria-label={intl.formatMessage({
				id: 'wacGlobalNavigation.nav-label',
				defaultMessage: 'Main',
			})}
			css={[navigationShellCss, mobileState.showMenu && expandedMobileShellCss]}
		>
			<div css={navigationBarContentCss}>
				<div css={navigationBarLogoCss}>
					<SkipToContent alignToLogo />
					<a
						data-testid="navigation-bar-logo-link"
						href={atlasUrl}
						aria-label={intl.formatMessage({
							id: 'wacGlobalNavigation.logo-label',
							defaultMessage: 'Atlassian logo',
						})}
						data-event="clicked"
						data-event-component="linkButton"
						data-event-container="globalNavWac"
						data-product-key={productKey}
						css={navigationBarLogoLink}
					>
						<span css={navigationBarLogoImageCss}>
							<LogoV3 brand="atlassian" type="logo" appearance="brand" width="100%" height="100%" />
						</span>
					</a>
				</div>
				{isOpenSearch ? (
					<div css={navigationBarSearchCss}>
						<SearchBar
							isOpenSearch={isOpenSearch}
							setIsOpenSearch={setIsOpenSearch}
							parentType="GlobalNav"
						/>
					</div>
				) : (
					<>
						<div css={desktopControlsCss}>
							<GlobalNavLinksContainer
								alignDropdownsToGlobalMenu
								globalLinksProps={globalLinksProps}
								globalLinksRef={globalLinksRef}
								maxLinks={maxLinks}
								showMore={showMore}
								setShowMore={setShowMore}
							/>
							{isShowSearchBarAndProfileContainer && !showMore && (
								<FeatureExposed
									experimentKey="wac-globalui-team24-globalnav-cta"
									group="test"
									conditions={[!isShowTryNowLink]}
								>
									<NavUtilities
										isShowTryNowLink={isShowTryNowLink}
										// @ts-expect-error -- Existing legacy CTA color prop.
										ctaButtonColor={tryNowLinkCtaButtonColor}
										productKey={productKey}
										isShowSearchButton={isShowSearchButton}
										setIsOpenSearch={setIsOpenSearch}
										searchAndProfileRef={searchAndProfileRef}
										navigationTrynowCtaUrl={tryNowLinkCtaUrl}
									/>
								</FeatureExposed>
							)}
						</div>
						<div css={mobileControlsCss}>
							<MobileNavTop
								showLogo={false}
								productKey={productKey}
								isLoggedIn={mobileState.userInfo.isUserLoggedIn}
								userName={mobileState.userInfo.name ?? ''}
								picture={mobileState.userInfo.picture ?? ''}
								handleClickAvatar={mobileState.handleClickAvatar}
								showMenu={mobileState.showMenu}
								handleToggleMenu={mobileState.handleToggleMenu}
								menuPage={mobileState.menuPage}
								handleMenuPage={mobileState.handleMenuPage}
								handleFirstOpen={mobileState.handleFirstOpen}
								isShowSearchButton={isShowSearchButton}
								isOpenSearch={isOpenSearch}
								setIsOpenSearch={setIsOpenSearch}
								tabletDropdownMenu={
									<GlobalNavMobileMenu productKey={productKey} state={mobileState} />
								}
							/>
						</div>
					</>
				)}
			</div>
			{mobileState.showMenu && (
				<div css={mobileMenuCss} id="nav-mobile-dropdown-menu">
					<GlobalNavMobileMenu productKey={productKey} state={mobileState} />
				</div>
			)}
		</nav>
	);
};

export const GlobalNav = ({
	productKey = 'home',
	isShowSearchBarAndProfileContainer = true,
	isShowTryNowLink = false,
	isShowSearchButton = true,
	tryNowLinkCtaButtonColor,
	isNavSticky = true,
	tryNowLinkCtaUrl,
	compoundNav,
}: GlobalNavProps) => {
	const intl = useIntl();
	const hasScrolled = useNavScrollShadow();

	const isAiTabEnabled = checkFeatureFlagGate(featureFlags.team26EuRefresh);
	const enterpriseHref = useLocalizedHref('/enterprise');

	const globalLinksProps: GlobalNavLinkContentProps[] = useMemo(
		() => [
			{
				globalLinksKey: 'products',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.products',
					defaultMessage: 'Products',
				}),
				hasDropdown: true,
				content: <GlobalNavProductsDropdown productKey={productKey} />,
				layout: GlobalNavDropdownLayout.Product,
			},
			...(isAiTabEnabled
				? [
						{
							globalLinksKey: 'ai',
							linkText: intl.formatMessage({
								id: 'globalNavigation.Navlink.ai',
								defaultMessage: 'AI',
							}),
							hasDropdown: true,
							content: <AiContent productKey={productKey} />,
							layout: GlobalNavDropdownLayout.AI,
						},
					]
				: []),
			{
				globalLinksKey: 'solutions',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.solutions',
					defaultMessage: 'Solutions',
				}),
				hasDropdown: true,
				content: <GlobalNavSolutionsDropdown productKey={productKey} />,
				layout: GlobalNavDropdownLayout.Solutions,
			},
			{
				globalLinksKey: 'why-atlassian',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.whyAtlassian',
					defaultMessage: 'Why Atlassian',
				}),
				hasDropdown: true,
				content: <GlobalNavWhyAtlassianDropdown productKey={productKey} />,
				layout: GlobalNavDropdownLayout.WhyAtlassian,
			},
			{
				globalLinksKey: 'resources',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.resources',
					defaultMessage: 'Resources',
				}),
				hasDropdown: true,
				content: <GlobalNavResourcesDropdown productKey={productKey} />,
				layout: GlobalNavDropdownLayout.Resources,
			},
			{
				globalLinksKey: 'enterprise',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.enterprise',
					defaultMessage: 'Enterprise',
				}),
				hasDropdown: false,
				href: enterpriseHref,
			},
		],
		// Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[enterpriseHref, intl, isAiTabEnabled, productKey],
	);

	const globalNavItemKeys = useMemo(() => {
		return globalLinksProps.map((globalLink) => globalLink.globalLinksKey);
	}, [globalLinksProps]);

	const navProductRecosResponse = useGlobalNavProductRecommendations();

	return (
		<UFOSegment name="global-nav-v2">
			<div
				data-nav-dropdown-boundary
				css={[
					css({
						position: 'relative',
						boxShadow: hasScrolled ? NAV_SCROLL_SHADOW : 'none',
						transition: 'box-shadow 160ms ease-out',
						'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
						zIndex: '5',
						backgroundColor: wacToken('color.neutral.light0'),
					}),
					isNavSticky && isStickyNavCss,
				]}
			>
				<NavProductRecommendationsContext.Provider value={navProductRecosResponse}>
					<GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
						<GlobalNavNavigationBar
							productKey={productKey}
							isShowSearchBarAndProfileContainer={isShowSearchBarAndProfileContainer}
							isShowTryNowLink={isShowTryNowLink}
							tryNowLinkCtaUrl={tryNowLinkCtaUrl}
							isShowSearchButton={isShowSearchButton}
							tryNowLinkCtaButtonColor={tryNowLinkCtaButtonColor}
							globalLinksProps={globalLinksProps}
						/>
						{compoundNav && <CompoundSubNav {...compoundNav} />}
					</GlobalNavProvider>
				</NavProductRecommendationsContext.Provider>
			</div>
		</UFOSegment>
	);
};

GlobalNav.displayName = 'GlobalNav';
